import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import { http, apiError, getSelectedPackage } from "../../lib/api";
import { track, trackOnce } from "../../lib/analytics";
import { useContent } from "../../lib/content";

const STORE = "rivanta_application";
export const REVENUE = ["Under $10,000", "$10,000–$25,000", "$25,000–$50,000", "$50,000–$100,000", "Over $100,000"];
export const SPEND = ["Under $1,000", "$1,000–$3,000", "$3,000–$5,000", "$5,000–$10,000", "Over $10,000"];
export const PACKAGE_OPTS = [["minimum", "Minimum — $1,000/month"], ["growth", "Growth — $3,000/month"], ["brand", "Brand Building — $5,000/month"], ["not_sure", "Not sure yet"]];
const INDUSTRIES = ["Roofing", "Window Cleaning", "Masonry", "Landscaping", "Pressure Washing", "Home Services / Contractor", "Other Local Service"];
const QUESTIONS = [
  "What's your name?",
  "What's the name of your business?",
  "How much revenue does your business currently generate per month?",
  "Are you currently running Facebook or Instagram ads?",
  "Which package are you interested in?",
  "What's the best way to reach you?",
  "You're One Step Closer to Growing Your Business.",
];
const EMPTY = { first_name: "", last_name: "", business_name: "", industry: "", revenue_range: "", runs_ads: "", ad_spend: "", package: "", phone: "", email: "", sms_consent: false };
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function load() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE) || "null");
    if (saved?.data) return { ...saved, data: { ...EMPTY, ...saved.data } };
  } catch { /* ignore */ }
  return { step: 0, data: { ...EMPTY }, id: newId() };
}

function validate(step, d) {
  const e = {};
  if (step === 0) { if (!d.first_name.trim()) e.first_name = "Please enter your first name."; if (!d.last_name.trim()) e.last_name = "Please enter your last name."; }
  if (step === 1) { if (!d.business_name.trim()) e.business_name = "Please enter your business name."; if (!d.industry) e.industry = "Please choose your industry."; }
  if (step === 2 && !d.revenue_range) e.revenue_range = "Please choose a revenue range.";
  if (step === 3) { if (!d.runs_ads) e.runs_ads = "Please choose Yes or No."; else if (d.runs_ads === "yes" && !d.ad_spend) e.ad_spend = "Please choose your monthly ad spend."; }
  if (step === 4 && !d.package) e.package = "Please choose a package (or Not sure yet).";
  if (step === 5) {
    const digits = d.phone.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
    if (digits.length !== 10) e.phone = "Please enter a valid 10-digit phone number.";
    if (!/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(d.email.trim())) e.email = "Please enter a valid email address.";
  }
  return e;
}

function Choice({ options, value, onPick, name, testPrefix }) {
  return (
    <div role="radiogroup" aria-label={name} className="grid gap-2.5 sm:grid-cols-2">
      {options.map(([val, label]) => {
        const on = value === val;
        return (
          <button type="button" key={val} role="radio" aria-checked={on} onClick={() => onPick(val)} data-testid={`${testPrefix}-${val.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            className={`flex items-center justify-between rounded-xl border px-4 py-3.5 text-left text-[15px] transition-[border-color,background-color] duration-200 ${on ? "border-electric bg-electric/15 text-white" : "border-white/[0.12] bg-white/[0.03] text-slate-200 hover:border-white/30"}`}>
            {label}
            <span className={`grid h-5 w-5 place-items-center rounded-full border ${on ? "border-electric bg-electric" : "border-white/25"}`}>{on && <Check className="h-3 w-3" />}</span>
          </button>
        );
      })}
    </div>
  );
}

const Err = ({ id, msg }) => (msg ? <p id={id} role="alert" className="mt-2 text-sm text-red-300">{msg}</p> : null);

function TextField({ id, label, value, onChange, error, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium text-slate-300">{label}</label>
      <input id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-err` : undefined}
        className="field" data-testid={`form-${id}-input`} {...rest} />
      <Err id={`${id}-err`} msg={error} />
    </div>
  );
}

export function QualificationForm() {
  const navigate = useNavigate();
  const { content } = useContent();
  const [state, setState] = useState(load);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const headingRef = useRef(null);
  const mounted = useRef(false);
  const { step, data } = state;
  const sms = content?.sms || {};

  useEffect(() => {
    const sel = getSelectedPackage();
    if (sel) setState((s) => ({ ...s, data: { ...s.data, package: sel } }));
    const onPkg = (e) => setState((s) => ({ ...s, data: { ...s.data, package: e.detail } }));
    window.addEventListener("rivanta:package", onPkg);
    return () => window.removeEventListener("rivanta:package", onPkg);
  }, []);
  useEffect(() => {
    try { localStorage.setItem(STORE, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);
  useEffect(() => {
    if (mounted.current) headingRef.current?.focus({ preventScroll: true });
    mounted.current = true;
  }, [step]);

  const set = (k, v) => {
    trackOnce("StartApplication", {}, state.id);
    setErrors((e) => ({ ...e, [k]: undefined }));
    setState((s) => ({ ...s, data: { ...s.data, [k]: v } }));
  };
  const goTo = (n) => setState((s) => ({ ...s, step: n }));
  const next = (override) => {
    const d = { ...data, ...(override || {}) };
    const e = validate(step, d);
    setErrors(e);
    if (Object.keys(e).length) return;
    trackOnce("StartApplication", {}, state.id);
    goTo(Math.min(6, step + 1));
  };
  const pick = (k, v, advance = true) => {
    set(k, v);
    if (advance) setTimeout(() => next({ [k]: v }), 220);
  };

  const submit = async () => {
    if (submitting) return;
    for (let s = 0; s < 6; s += 1) {
      const e = validate(s, data);
      if (Object.keys(e).length) { setErrors(e); goTo(s); return; }
    }
    setSubmitting(true);
    setSubmitError("");
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "fbclid", "gclid"].forEach((k) => { if (params.get(k)) utm[k] = params.get(k); });
    try {
      await http.post("/leads", {
        ...data, ad_spend: data.runs_ads === "yes" ? data.ad_spend : null, submission_id: state.id, event_id: state.id,
        sms_disclosure: data.sms_consent ? `${sms.consent_label || ""} ${sms.disclosure || ""}`.trim() : "",
        source_page: window.location.pathname, utm, website: "",
      });
      const leadKey = `rivanta_lead_${state.id}`;
      if (!sessionStorage.getItem(leadKey)) {
        sessionStorage.setItem(leadKey, "1");
        track("Lead", { package: data.package }, state.id);
      }
      try { localStorage.removeItem(STORE); } catch { /* ignore */ }
      navigate("/thank-you", { state: { submitted: true } });
    } catch (err) {
      setSubmitError(apiError(err));
      setSubmitting(false);
    }
  };

  const progress = Math.round((step / 6) * 100);
  const onSubmitForm = (e) => { e.preventDefault(); if (step === 6) submit(); else next(); };

  return (
    <form onSubmit={onSubmitForm} noValidate className="relative overflow-hidden rounded-[28px] border border-white/10 bg-panel/80 p-6 shadow-[0_40px_120px_-50px_rgba(0,136,255,0.7)] sm:p-10" data-testid="qualification-form">
      <div className="flex items-center justify-between font-mono text-xs text-slate-400">
        <span data-testid="form-step-indicator">Step {Math.min(step + 1, 7)} of 7</span>
        <span>{progress}%</span>
      </div>
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Application progress">
        <motion.div className="h-full bg-gradient-to-r from-electric to-cyan-300" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
      </div>

      <AnimatePresence initial={false}>
        <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="mt-8 min-h-[300px]">
          <h3 ref={headingRef} tabIndex={-1} className="text-2xl sm:text-3xl font-semibold tracking-tight text-white outline-none" data-testid="form-question">{QUESTIONS[step]}</h3>
          <div className="mt-7 space-y-5">
            {step === 0 && (
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField id="first_name" label="First name" value={data.first_name} onChange={(v) => set("first_name", v)} error={errors.first_name} autoComplete="given-name" maxLength={60} />
                <TextField id="last_name" label="Last name" value={data.last_name} onChange={(v) => set("last_name", v)} error={errors.last_name} autoComplete="family-name" maxLength={60} />
              </div>
            )}
            {step === 1 && (
              <div className="grid gap-4">
                <TextField id="business_name" label="Business name" value={data.business_name} onChange={(v) => set("business_name", v)} error={errors.business_name} autoComplete="organization" maxLength={120} />
                <div>
                  <label htmlFor="industry" className="mb-2 block text-sm font-medium text-slate-300">Industry</label>
                  <select id="industry" value={data.industry} onChange={(e) => set("industry", e.target.value)} className="field appearance-none" data-testid="form-industry-select"
                    aria-invalid={Boolean(errors.industry)} aria-describedby={errors.industry ? "industry-err" : undefined}>
                    <option value="" className="bg-ink">Select your industry</option>
                    {INDUSTRIES.map((i) => <option key={i} value={i} className="bg-ink">{i}</option>)}
                  </select>
                  <Err id="industry-err" msg={errors.industry} />
                </div>
              </div>
            )}
            {step === 2 && (<><Choice name="Monthly revenue" testPrefix="form-revenue" options={REVENUE.map((r) => [r, r])} value={data.revenue_range} onPick={(v) => pick("revenue_range", v)} /><Err msg={errors.revenue_range} /></>)}
            {step === 3 && (
              <>
                <Choice name="Running ads" testPrefix="form-runs-ads" options={[["yes", "Yes"], ["no", "No"]]} value={data.runs_ads} onPick={(v) => pick("runs_ads", v, v === "no")} />
                <Err msg={errors.runs_ads} />
                {data.runs_ads === "yes" && (
                  <div className="pt-3">
                    <p className="mb-3 text-base font-medium text-white">How much are you currently spending on ads each month?</p>
                    <Choice name="Monthly ad spend" testPrefix="form-spend" options={SPEND.map((s) => [s, s])} value={data.ad_spend} onPick={(v) => pick("ad_spend", v)} />
                    <Err msg={errors.ad_spend} />
                  </div>
                )}
              </>
            )}
            {step === 4 && (<><Choice name="Package" testPrefix="form-package" options={PACKAGE_OPTS} value={data.package} onPick={(v) => pick("package", v)} /><Err msg={errors.package} /></>)}
            {step === 5 && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField id="phone" label="Phone number" type="tel" inputMode="tel" value={data.phone} onChange={(v) => set("phone", v)} error={errors.phone} autoComplete="tel" placeholder="(406) 555-0123" maxLength={20} />
                  <TextField id="email" label="Email address" type="email" value={data.email} onChange={(v) => set("email", v)} error={errors.email} autoComplete="email" placeholder="you@business.com" maxLength={200} />
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <label className="flex cursor-pointer gap-3">
                    <input type="checkbox" checked={data.sms_consent} onChange={(e) => set("sms_consent", e.target.checked)} data-testid="form-sms-consent-checkbox"
                      className="mt-1 h-5 w-5 shrink-0 accent-[#0088FF]" aria-describedby="sms-disclosure" />
                    <span className="text-sm font-medium text-white">{sms.consent_label || "Yes, I'd like to receive marketing text messages from Rivanta Labs."} <span className="font-normal text-slate-400">(optional)</span></span>
                  </label>
                  <p id="sms-disclosure" className="mt-2 pl-8 text-xs leading-relaxed text-slate-400" data-testid="form-sms-disclosure">
                    {sms.disclosure} See our <Link to="/privacy-policy" className="underline">Privacy Policy</Link> and <Link to="/terms" className="underline">Terms</Link>.
                  </p>
                </div>
              </>
            )}
            {step === 6 && (
              <>
                <p className="text-base leading-relaxed text-slate-300">Submit your information and we'll reach out to learn more about your business and see if we're a good fit.</p>
                <dl className="grid gap-x-6 gap-y-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm sm:grid-cols-2" data-testid="form-review">
                  {[
                    ["Name", `${data.first_name} ${data.last_name}`, 0], ["Business", `${data.business_name} · ${data.industry}`, 1],
                    ["Monthly revenue", data.revenue_range, 2], ["Current ad spend", data.runs_ads === "yes" ? data.ad_spend : "Not running ads", 3],
                    ["Package", (PACKAGE_OPTS.find(([v]) => v === data.package) || [])[1], 4], ["Contact", `${data.phone} · ${data.email}`, 5],
                  ].map(([k, v, s]) => (
                    <div key={k} className="min-w-0">
                      <dt className="flex items-center justify-between text-xs text-slate-400">{k}<button type="button" onClick={() => goTo(s)} className="text-cyan-300 hover:underline" data-testid={`form-edit-step-${s + 1}`}>Edit</button></dt>
                      <dd className="mt-0.5 truncate text-white">{v}</dd>
                    </div>
                  ))}
                </dl>
                {submitError && <p role="alert" className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200" data-testid="form-submit-error">{submitError}</p>}
                <p className="flex items-center gap-2 text-xs text-slate-400"><Lock className="h-3.5 w-3.5" /> Your information is sent securely and never sold.</p>
              </>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between gap-3 border-t border-white/[0.07] pt-6">
        <button type="button" onClick={() => goTo(Math.max(0, step - 1))} disabled={step === 0 || submitting} data-testid="form-back-btn"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-slate-300 transition-colors hover:text-white disabled:opacity-30">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < 6 ? (
          <button type="submit" className="btn-primary" data-testid="form-next-btn">Continue <ArrowRight className="h-4 w-4" /></button>
        ) : (
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="form-submit-btn">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Request My Call <ArrowRight className="h-4 w-4" /></>}
          </button>
        )}
      </div>
    </form>
  );
}
